<template>
  <b-sidebar
    id="add-new-user-sidebar"
    :visible="isAddNewUserSidebarActive"
    :bg-variant="COLOR_VARIANTS.WHITE"
    sidebar-class="sidebar-lg"
    shadow
    backdrop
    no-header
    right
    width="50%"
    @hidden="resetForm"
    @change="val => $emit('update:is-add-new-user-sidebar-active', val)"
  >
    <template #default="{ hide }">
      <!-- Header -->
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
      >
        <h5 class="mb-0">
          {{ $t('application.addUserModal.title') }}
        </h5>

        <feather-icon class="ml-1 cursor-pointer" icon="XIcon" size="16" @click="hide" />
      </div>

      <!-- BODY -->
      <validation-observer #default="{ handleSubmit }" ref="refFormObserver">
        <!-- Form -->
        <b-form class="p-2" @submit.prevent="handleSubmit(onSubmit)" @reset.prevent="resetForm">
          <b-row>
            <b-col md="6">
              <!-- First Name -->
              <validation-provider #default="validationContext" name="First name" rules="required">
                <b-form-group
                  :label="$t('application.addUserModal.labels.firstName')"
                  label-for="first-name"
                >
                  <b-form-input
                    id="first-name"
                    v-model="userData.firstName"
                    autofocus
                    :state="getValidationState(validationContext)"
                    trim
                    placeholder=""
                  />

                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>

            <b-col md="6">
              <!-- Last Name -->
              <validation-provider
                #default="validationContext"
                name="Last name"
                rules="required|alpha-num"
              >
                <b-form-group
                  :label="$t('application.addUserModal.labels.lastName')"
                  label-for="last-name"
                >
                  <b-form-input
                    id="last-name"
                    v-model="userData.lastName"
                    :state="getValidationState(validationContext)"
                    trim
                  />

                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>
          </b-row>

          <b-row>
            <b-col md="6">
              <!-- Birthday -->
              <b-form-group
                :label="$t('application.addUserModal.labels.birthday')"
                label-for="birthday"
              >
                <validation-provider #default="{ errors }" name="Birthday" rules="required">
                  <b-form-input
                    v-model="userData.birthday"
                    v-mask="['##.##.####']"
                    placeholder="DD.MM.YYYY"
                    :state="errors.length > 0 || dateInvalid(userData.birthday) ? false : null"
                  />
                  <small class="text-danger">{{
                    errors[0] || dateInvalid(userData.birthday)
                  }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col md="6">
              <!-- Mobile -->
              <validation-provider #default="validationContext" name="Phone" rules="required">
                <b-form-group
                  :label="$t('application.addUserModal.labels.phone')"
                  label-for="phone"
                >
                  <b-form-input
                    id="phone"
                    v-model="userData.mobile"
                    type="number"
                    :state="getValidationState(validationContext)"
                    trim
                  />

                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>

            <b-col md="6">
              <!-- Email -->
              <validation-provider #default="validationContext" name="Email" rules="required|email">
                <b-form-group
                  :label="$t('application.addUserModal.labels.email')"
                  label-for="email"
                >
                  <b-form-input
                    id="email"
                    v-model="userData.email"
                    :state="getValidationState(validationContext)"
                    trim
                  />

                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>
          </b-row>

          <!-- Address -->
          <b-row>
            <!-- Street address -->
            <b-col md="6">
              <b-form-group
                :label="$t('application.addUserModal.labels.street')"
                label-for="street"
              >
                <validation-provider #default="validationContext" name="Street" rules="required">
                  <b-form-input
                    id="street"
                    v-model="userData.address.streetAddress"
                    :state="getValidationState(validationContext)"
                    placeholder=""
                  />
                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </validation-provider>
              </b-form-group>
            </b-col>

            <!-- City address-->
            <b-col md="6">
              <b-form-group :label="$t('application.addUserModal.labels.city')" label-for="city">
                <validation-provider #default="validationContext" name="City" rules="required">
                  <b-form-input
                    id="city"
                    v-model="userData.address.city"
                    :state="getValidationState(validationContext)"
                    placeholder=""
                  />
                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </validation-provider>
              </b-form-group>
            </b-col>

            <!-- Postcode address-->
            <b-col md="6">
              <b-form-group
                :label="$t('application.addUserModal.labels.postcode')"
                label-for="postcode"
              >
                <validation-provider #default="validationContext" name="Postcode" rules="required">
                  <b-form-input
                    id="postcode"
                    v-model="userData.address.postcode"
                    :state="getValidationState(validationContext)"
                    placeholder=""
                    type="number"
                  />
                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </validation-provider>
              </b-form-group>
            </b-col>

            <!-- State address-->
            <b-col md="6">
              <validation-provider #default="{ errors }" name="State" rules="required">
                <b-form-group
                  :label="$t('state')"
                  label-for="state"
                  :state="errors.length > 0 ? false : null"
                >
                  <v-select
                    id="state"
                    v-model="userData.address.state"
                    :options="stateOptions"
                    :state="errors.length > 0 ? false : null"
                    label="text"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>
          </b-row>
          <!-- End Address -->

          <b-row>
            <!-- Love -->
            <b-col md="6">
              <b-form-group :label="$t('application.addUserModal.labels.love')" label-for="love">
                <validation-provider #default="validationContext" name="Love" rules="required">
                  <b-form-checkbox
                    v-model="userData.love"
                    name="love"
                    switch
                    inline
                    :state="userData.love ? true : null"
                  >
                    <span class="switch-icon-left">
                      <feather-icon icon="CheckIcon" />
                    </span>
                    <span class="switch-icon-right">
                      <feather-icon icon="XIcon" />
                    </span>
                  </b-form-checkbox>
                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </validation-provider>
              </b-form-group>
            </b-col>
          </b-row>
          <!-- Profession  -->
          <b-row>
            <b-col md="6">
              <validation-provider #default="{ errors }" name="ammatti" rules="required">
                <b-form-group
                  :label="$t('application.addUserModal.labels.profession')"
                  label-for="role"
                  :state="errors.length > 0 ? false : null"
                >
                  <v-select
                    id="role"
                    v-model="userData.role"
                    style="text-transform: capitalize"
                    :state="errors.length > 0 ? false : null"
                    :options="typeReqOptions"
                    :selectable="option => !option.includes('valitse')"
                    label="text"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>
            <b-col md="6">
              <b-form-group
                v-if="userData.role == 'muu'"
                label="Mikä?"
                label-for="other-profession"
              >
                <validation-provider #default="{ errors }" name="ammatti" rules="required">
                  <b-form-input
                    id="other-profession"
                    v-model="otherProfession"
                    :state="errors.length > 0 ? false : null"
                    placeholder=""
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
              <b-row v-if="userData.role == 'opiskelija'">
                <b-col md="6">
                  <b-form-group label="Mitä opiskelet?" label-for="study-subject">
                    <validation-provider
                      #default="{ errors }"
                      name="tutkimustyyppi"
                      rules="required"
                    >
                      <b-form-input
                        id="study-subject"
                        v-model="userData.studies.studyType"
                        :state="errors.length > 0 ? false : null"
                        placeholder=""
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group label="Opintopisteet" label-for="credit">
                    <validation-provider
                      #default="{ errors }"
                      name="opintopisteet"
                      rules="required"
                    >
                      <b-form-input
                        id="credits"
                        v-model="userData.studies.credits"
                        :state="errors.length > 0 ? false : null"
                        placeholder="Esim: 20"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
              </b-row>
            </b-col>
          </b-row>

          <b-row>
            <b-col md="6">
              <validation-provider
                #default="{ errors }"
                :name="$t('application.addUserModal.labels.finnishLanguage')"
                rules="required"
              >
                <b-form-group
                  :label="$t('application.addUserModal.labels.finnishLanguage')"
                  label-for="finnish-language"
                  :state="errors.length > 0 ? false : null"
                >
                  <v-select
                    id="finnish-language"
                    v-model="userData.finnish"
                    :state="errors.length > 0 ? false : null"
                    :options="finnishLevelOptions"
                    :selectable="option => !option.value.includes('valitse')"
                    label="text"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>
            <b-col md="6">
              <b-form-group
                :label="$t('application.addUserModal.labels.languages')"
                label-for="languages"
              >
                <v-select
                  id="languages"
                  v-model="userData.languages"
                  :options="languagesOptions"
                  :selectable="option => !option.value.includes('valitse')"
                  label="text"
                  multiple
                />
              </b-form-group>
            </b-col>

            <b-col md="6">
              <b-form-group
                :label="$t('application.addUserModal.labels.carAvailable')"
                label-for="car-available"
              >
                <b-form-checkbox
                  v-model="userData.carAvailable"
                  name="car-available"
                  switch
                  inline
                  :state="userData.carAvailable ? true : null"
                >
                  <span class="switch-icon-left">
                    <feather-icon icon="CheckIcon" />
                  </span>
                  <span class="switch-icon-right">
                    <feather-icon icon="XIcon" />
                  </span>
                </b-form-checkbox>
              </b-form-group>
            </b-col>

            <b-col md="6">
              <b-form-group
                :label="$t('application.addUserModal.labels.drivingLicense')"
                label-for="driving-license"
              >
                <v-select
                  id="driving-license"
                  v-model="userData.drivingLicense"
                  :options="drivingLicenseOptions"
                  :selectable="option => !option.value.includes('valitse')"
                  label="text"
                  multiple
                />
              </b-form-group>
            </b-col>
          </b-row>

          <!-- About -->
          <b-row>
            <b-col md="12">
              <b-form-group :label="$t('application.addUserModal.labels.about')" label-for="about">
                <b-form-textarea
                  id="about"
                  v-model="userData.about"
                  :placeholder="$t('application.addUserModal.placeholders.about')"
                  rows="3"
                  max-rows="6"
                  style="margin-bottom: 20px"
                />
              </b-form-group>
            </b-col>
          </b-row>

          <!-- WorkHistory -->
          <b-row>
            <b-col md="12">
              <b-form-group
                :label="$t('application.addUserModal.labels.workHistory')"
                label-for="workhistory"
              >
                <b-form-textarea
                  id="workhistory"
                  v-model="userData.workHistory"
                  :placeholder="$t('application.addUserModal.placeholders.workHistory')"
                  rows="3"
                  max-rows="6"
                  style="margin-bottom: 20px"
                />
              </b-form-group>
            </b-col>
          </b-row>

          <!-- Form Actions -->
          <div class="d-flex mt-2">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              :variant="COLOR_VARIANTS.PRIMARY"
              class="mr-2"
              type="submit"
              :disabled="isLoading"
            >
              {{ $t('Add') }}
            </b-button>
            <b-button
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              type="button"
              :variant="COLOR_VARIANTS.OUTLINE_SECONDARY"
              @click="hide"
            >
              {{ $t('cancel') }}
            </b-button>
          </div>
        </b-form>
      </validation-observer>
    </template>
  </b-sidebar>
</template>

<script>
/* eslint-disable */
import {
  BSidebar,
  BForm,
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
  BButton,
  BRow,
  BCol,
  BFormCheckbox,
  BFormTextarea,
  BFormRadio,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { ref } from '@vue/composition-api'
import { required, alphaNum, email } from '@validations'
import { COLOR_VARIANTS } from '@/shared/constants'
import formValidation from '@hw-ui/comp-functions/forms/form-validation'
import Ripple from 'vue-ripple-directive'
import vSelect from 'vue-select'
import countries from '@mock/data/other/countries'
import moment from 'moment'
import { mask } from 'vue-the-mask'

export default {
  components: {
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BButton,
    BRow,
    BCol,
    BFormCheckbox,
    BFormTextarea,
    BFormRadio,

    vSelect,

    // Form Validation
    ValidationProvider,
    ValidationObserver,
  },
  directives: {
    Ripple,
    mask,
  },
  model: {
    prop: 'isAddNewUserSidebarActive',
    event: 'update:is-add-new-user-sidebar-active',
  },
  props: {
    isAddNewUserSidebarActive: {
      type: Boolean,
      required: true,
    },
    planOptions: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      required,
      alphaNum,
      email,
      countries,
      COLOR_VARIANTS,
    }
  },
  computed: {
    typeReqOptions() {
      const roles = this.$store.state.appAccount.roles.map(role => role.label)
      roles.push('opiskelija', 'muu')
      return roles
    },
  },
  setup(props, { emit }) {
    const blankUserData = {
      firstName: '',
      lastName: '',
      birthday: '',
      mobile: '',
      email: '',
      address: {
        city: '',
        streetAddress: '',
        postcode: '',
        state: '',
      },
      profession: '',
      love: false,
      about: '',
      workHistory: '',
      role: '',
      studies: {},
      gender: '',
      finnish: '',
      languages: [],
      carAvailable: false,
      drivingLicense: [],
    }
    const otherProfession = ref(null)
    const isLoading = ref(false)

    const dateFormat = 'DD.MM.YYYY'

    const stateOptions = [
      { text: 'Uusimaa', value: 'uusimaa' },
      { text: 'Varsinais-Suomi', value: 'varsinais-suomi' },
      { text: 'Satakunta', value: 'satakunta' },
      { text: 'Päijät-Häme', value: 'päijät-häme' },
      { text: 'Pohjois-Savo', value: 'pohjois-savo' },
      { text: 'Pohjois-Pohjanmaa', value: 'pohjois-pohjanmaa' },
      { text: 'Pohjois-Karjala', value: 'pohjois-karjala' },
      { text: 'Pohjanmaa', value: 'pohjanmaa' },
      { text: 'Pirkanmaa', value: 'pirkanmaa' },
      { text: 'Lappi', value: 'lappi' },
      { text: 'Kymenlaakso', value: 'kymenlaakso' },
      { text: 'Keski-Suomi', value: 'keski-suomi' },
      { text: 'Keski-Pohjanmaa', value: 'keski-pohjanmaa' },
      { text: 'Kanta-Häme', value: 'kanta-häme' },
      { text: 'Kainuu', value: 'kainuu' },
      { text: 'Etelä-Savo', value: 'etelä-savo' },
      { text: 'Etelä-Pohjanmaa', value: 'etelä-pohjanmaa' },
      { text: 'Etelä-Karjala', value: 'etelä-karjala' },
      { text: 'Ahvenanmaa', value: 'ahvenanmaa' },
    ]

    const finnishLevelOptions = [
      { text: 'Valitse', value: 'valitse' },
      { text: 'Äidinkieli', value: 'äidinkieli' },
      { text: 'Erinomainen', value: 'erinomainen' },
      { text: 'Hyvä', value: 'hyvä' },
      { text: 'Perus', value: 'perus' },
      { text: 'Alkeet', value: 'alkeet' },
      { text: 'Ei', value: 'ei' },
    ]

    const languagesOptions = [
      { value: 'valitse', text: 'Valitse' },
      { value: 'english', text: 'English' },
      { value: 'swedish', text: 'Swedish' },
      { value: 'somali', text: 'Somali' },
      { value: 'arabic', text: 'Arabic' },
      { value: 'spanish', text: 'Spanish' },
      { value: 'russian', text: 'Russian' },
    ]

    const drivingLicenseOptions = [
      { text: 'B', value: 'b' },
      { text: 'C', value: 'c' },
      { text: 'A', value: 'a' },
    ]

    const userData = ref(JSON.parse(JSON.stringify(blankUserData)))
    const resetUserData = () => {
      userData.value = JSON.parse(JSON.stringify(blankUserData))
      isLoading.value = false
    }

    const onSubmit = () => {
      // Parse selection data
      isLoading.value = true
      userData.value.firstName = userData.value.firstName.toLowerCase()
      userData.value.lastName = userData.value.lastName.toLowerCase()
      userData.value.address.city = userData.value.address.city.toLowerCase()
      userData.value.address.state = userData.value.address.state.value
      userData.value.fullName = `${userData.value.firstName} ${userData.value.lastName}`
      userData.value.status = 'pending'
      userData.value.confirmed = false
      userData.value.isAdmin = false
      if (userData.value.role !== 'muu') {
        userData.value.profession = userData.value.role
      } else {
        userData.value.profession = otherProfession.value.toLowerCase()
      }
      userData.value.finnish = userData.value.finnish.value
      userData.value.languages = userData.value.languages.map(item => item.value)
      userData.value.drivingLicense = userData.value.drivingLicense.map(item => item.value)
      emit('add-application', userData.value)
      emit('update:is-add-new-user-sidebar-active', false)
    }

    const dateInvalid = date => {
      if (!date || moment(date, dateFormat, true).isValid()) {
        return ''
      }
      return 'Päivämäärä pitää olla muodossa, esim: 01.01.1990'
    }

    const { refFormObserver, getValidationState, resetForm } = formValidation(resetUserData)

    return {
      userData,
      onSubmit,
      stateOptions,
      otherProfession,
      isLoading,
      finnishLevelOptions,
      languagesOptions,
      drivingLicenseOptions,

      refFormObserver,
      getValidationState,
      resetForm,
      dateInvalid,
    }
  },
}
</script>

<style lang="scss">
@import '@hw-ui/scss/vue/libs/vue-select.scss';

#add-new-user-sidebar {
  .vs__dropdown-menu {
    max-height: 200px !important;
  }
}
</style>
